import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, Mail, Lock, LogIn, ArrowLeft, Send } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { useAuthForm } from '@/contexts/AuthFormProvider';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import config from '@/config';

const LoginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters')
});

const ForgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address')
});

const ConfirmForgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  confirmationCode: z.string().min(1, 'Confirmation code is required'),
  newPassword: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string().min(6, 'Password confirmation is required')
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});


const LoginForm: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const { toast } = useToast();
  const { formType, showRegisterForm, showLoginForm, showForgotPasswordForm, showConfirmForgotPasswordForm, closeForm } = useAuthForm();
  const navigate = useNavigate();

  // Debug logs for mobile styling
  useEffect(() => {
    console.log('🔍 LoginForm mounted, current form type:', formType);
    console.log('🔍 User agent:', navigator.userAgent);
    console.log('🔍 Window dimensions:', window.innerWidth, 'x', window.innerHeight);
    console.log('🔍 Screen dimensions:', screen.width, 'x', screen.height);
    console.log('🔍 Device pixel ratio:', window.devicePixelRatio);
  }, [formType]);

  const getSchema = () => {
    switch (formType) {
      case 'login':
        return LoginSchema;
      case 'forgotPassword':
        return ForgotPasswordSchema;
      case 'confirmForgotPassword':
        return ConfirmForgotPasswordSchema;
      default:
        return LoginSchema;
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm({
    resolver: zodResolver(getSchema())
  });

  // Handle form state when switching between modes
  useEffect(() => {
    if (formType === 'confirmForgotPassword' && forgotPasswordEmail) {
      // Case 1: User came from forgot password form - pre-fill email  
      reset({
        email: forgotPasswordEmail,
        confirmationCode: '',
        newPassword: '',
        confirmPassword: ''
      });
    } else if (formType === 'confirmForgotPassword' && !forgotPasswordEmail) {
      // Case 2: User clicked "Already have a reset code?" - clear form
      reset({
        email: '',
        confirmationCode: '',
        newPassword: '',
        confirmPassword: ''
      });
    } else {
      // Reset form when switching to other modes
      reset();
    }
  }, [formType, forgotPasswordEmail, reset]);

  const onSubmit = async (data: any) => {
    setIsLoading(true);
    setError(''); // Clear previous errors
    
    try {
      if (formType === 'login') {
        const response = await axios.post(
          `${config.NEXT_PUBLIC_BASE_URL}/auth/login`,
          { email: data.email, password: data.password },
          {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.status === 200 && response.data.accessToken) {
          localStorage.setItem('accessToken', response.data.accessToken);
          toast({
            title: 'Welcome back!',
            description: 'You have been successfully logged in.',
          });
          closeForm();
          navigate('/dashboard');
        }
      } else if (formType === 'forgotPassword') {
        const response = await axios.post(
          `${config.NEXT_PUBLIC_BASE_URL}/auth/forgot-password`,
          { email: data.email },
          {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (response.status === 200) {
          setForgotPasswordEmail(data.email);
          toast({
            title: 'Reset Code Sent',
            description: 'Please check your email (including spam folder) for the password reset code.',
          });
          showConfirmForgotPasswordForm();
        }
      } else if (formType === 'confirmForgotPassword') {
        const response = await axios.post(
          `${config.NEXT_PUBLIC_BASE_URL}/auth/confirm-forgot-password`,
          {
            email: data.email,
            confirmationCode: data.confirmationCode,
            newPassword: data.newPassword
          },
          {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
            },
          }
        );
        
        if (response.status === 200) {
          toast({
            title: 'Password Reset Successful',
            description: 'You can now log in with your new password.',
          });
          showLoginForm();
        }
      }
    } catch (error: any) {
      let errorMessage = '';
      
      if (formType === 'login') {
        errorMessage = (Array.isArray(error?.response?.data?.errors) && error.response.data.errors.length > 0)
          ? error.response.data.errors.join(", ")
          : (error?.response?.data?.message || "Login failed. Please try again.");
      } else if (formType === 'forgotPassword') {
        errorMessage = error?.response?.data?.message || 'Failed to send reset code. Please try again.';
      } else if (formType === 'confirmForgotPassword') {
        errorMessage = error?.response?.data?.message || 'Failed to reset password. Please check your code and try again.';
      }
      
      setError(errorMessage);
      console.error('Auth error:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Additional debug effect to check styles after render
  useEffect(() => {
    const timer = setTimeout(() => {
      const emailInput = document.querySelector('input[type="email"]') as HTMLInputElement;
      if (emailInput) {
        const computedStyles = window.getComputedStyle(emailInput);
        console.log('🔍 Email input computed styles:');
        console.log('  backgroundColor:', computedStyles.backgroundColor);
        console.log('  backdropFilter:', computedStyles.backdropFilter);
        console.log('  border:', computedStyles.border);
        console.log('  borderRadius:', computedStyles.borderRadius);
        console.log('  color:', computedStyles.color);
        console.log('  WebkitAppearance:', computedStyles.webkitAppearance);
      }
      
      const formContainer = document.querySelector('.bg-white\\/5');
      if (formContainer) {
        const containerStyles = window.getComputedStyle(formContainer);
        console.log('🔍 Form container computed styles:');
        console.log('  backgroundColor:', containerStyles.backgroundColor);
        console.log('  backdropFilter:', containerStyles.backdropFilter);
      }
    }, 100);
    
    return () => clearTimeout(timer);
  }, [formType]);

  return (
    <div 
      className="w-full max-w-md mx-auto bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl"
      style={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(16px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '16px',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
      }}
    >
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8">
        <div className="flex items-center justify-center gap-3 mb-4">
          {(formType === 'forgotPassword' || formType === 'confirmForgotPassword') && (
            <button
              type="button"
              onClick={() => showLoginForm()}
              className="text-blue-400 hover:text-blue-300 transition-colors duration-200"
            >
              <ArrowLeft size={20} />
            </button>
          )}
          <motion.h2 
            className="text-2xl sm:text-3xl font-bold text-white"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {formType === 'login' ? 'Welcome Back' : formType === 'forgotPassword' ? 'Reset Password' : 'Enter Reset Code'}
          </motion.h2>
        </div>
        <motion.p 
          className="text-sm sm:text-base text-gray-300 px-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {formType === 'login' ? 'Sign in to continue building with ShredsAI' : formType === 'forgotPassword' ? 'Enter your email to receive a reset code' : forgotPasswordEmail ? `Enter the code sent to ${forgotPasswordEmail} and set a new password` : 'Enter your email and the reset code to set a new password'}
        </motion.p>
      </div>

      {/* Error Display */}
      {error && (
        <motion.div
          className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <p className="text-red-400 text-sm font-medium">{error}</p>
        </motion.div>
      )}

      {/* Form */}
      <motion.form
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-4 sm:space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        {/* Email Field */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-200">Email</label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              {...register('email')}
              type="email"
              className="w-full pl-10 sm:pl-12 pr-4 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-base"
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(8px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                WebkitAppearance: 'none',
                appearance: 'none'
              }}
              placeholder="Enter your email"
              onFocus={() => console.log('🔍 Email input focused - checking if styles are applied')}
              onBlur={() => console.log('🔍 Email input blurred')}
            />
          </div>
          {errors.email && (
            <p className="text-red-400 text-sm">{String(errors.email?.message)}</p>
          )}
        </div>
        
        {/* Info message for confirmForgotPassword */}
        {formType === 'confirmForgotPassword' && (
          <motion.div
            className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <p className="text-blue-300 text-sm">
              {forgotPasswordEmail ? (
                <>📧 We've sent a reset code to <strong>{forgotPasswordEmail}</strong>. Please check your email (including spam folder) and enter the code below.</>
              ) : (
                <>📧 Enter your email and the reset code you received. If you don't have a code yet, please check your email (including spam folder).</>
              )}
            </p>
          </motion.div>
        )}

        {/* Password Field - only for login */}
        {formType === 'login' && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-200">Password</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-base"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '16px',
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
                placeholder="Enter your password"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors duration-200"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && (
              <p className="text-red-400 text-sm">{String(errors.password?.message)}</p>
            )}
          </div>
        )}
        
        {/* Confirmation Code Field - for confirmForgotPassword */}
        {formType === 'confirmForgotPassword' && (
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-200">Confirmation Code</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-gray-400" />
              </div>
              <input
                {...register('confirmationCode')}
                type="text"
                className="w-full pl-12 pr-4 py-3 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '16px',
                  WebkitAppearance: 'none',
                  appearance: 'none'
                }}
                placeholder="Enter code from email"
              />
            </div>
            {errors.confirmationCode && (
              <p className="text-red-400 text-sm">{String(errors.confirmationCode?.message)}</p>
            )}
          </div>
        )}
        
        {/* New Password Fields - for confirmForgotPassword */}
        {formType === 'confirmForgotPassword' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-200">New Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('newPassword')}
                  type={showPassword ? 'text' : 'password'}
                  className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-base"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '16px',
                    WebkitAppearance: 'none',
                    appearance: 'none'
                  }}
                  placeholder="Enter your new password"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="text-red-400 text-sm">{String(errors.newPassword?.message)}</p>
              )}
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-200">Confirm New Password</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('confirmPassword')}
                  type={showPassword ? 'text' : 'password'}
                  className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-3 sm:py-4 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 text-base"
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(8px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: 'white',
                    fontSize: '16px',
                    WebkitAppearance: 'none',
                    appearance: 'none'
                  }}
                  placeholder="Confirm your new password"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-white transition-colors duration-200"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-red-400 text-sm">{String(errors.confirmPassword?.message)}</p>
              )}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isLoading}
          className="w-full py-3 sm:py-4 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold text-base sm:text-lg rounded-xl hover:from-blue-500 hover:to-purple-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-transparent disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center space-x-2"
          whileHover={{ scale: isLoading ? 1 : 1.02 }}
          whileTap={{ scale: isLoading ? 1 : 0.98 }}
        >
          {isLoading ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              {formType === 'login' && <LogIn className="w-5 h-5" />}
              {formType === 'forgotPassword' && <Send className="w-5 h-5" />}
              {formType === 'confirmForgotPassword' && <Lock className="w-5 h-5" />}
              <span>
                {formType === 'login' ? 'Sign In' : formType === 'forgotPassword' ? 'Send Reset Code' : 'Reset Password'}
              </span>
            </>
          )}
        </motion.button>

        {/* Navigation Links */}
        <div className="text-center space-y-2">
          {formType === 'login' && (
            <>
              <p className="text-gray-300">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={showRegisterForm}
                  className="text-blue-400 hover:text-blue-300 font-semibold transition-colors duration-200"
                >
                  Sign up
                </button>
              </p>
              <p className="text-gray-300">
                <button
                  type="button"
                  onClick={showForgotPasswordForm}
                  className="text-blue-400 hover:text-blue-300 font-semibold transition-colors duration-200"
                >
                  Forgot your password?
                </button>
              </p>
            </>
          )}
          
          {formType === 'forgotPassword' && (
            <p className="text-gray-300">
              <button
                type="button"
                onClick={showConfirmForgotPasswordForm}
                className="text-blue-400 hover:text-blue-300 font-semibold transition-colors duration-200"
              >
                Already have a reset code?
              </button>
            </p>
          )}
          
          {formType === 'confirmForgotPassword' && (
            <p className="text-gray-300">
              <button
                type="button"
                onClick={showForgotPasswordForm}
                className="text-blue-400 hover:text-blue-300 font-semibold transition-colors duration-200"
              >
                Didn't receive the code? Send again
              </button>
            </p>
          )}
        </div>
      </motion.form>

      {/* Terms */}
      <motion.p
        className="text-xs text-gray-400 text-center mt-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        By signing in, you agree to our{' '}
        <a href="/privacy" className="text-blue-400 hover:text-blue-300">
          Terms of Service
        </a>{' '}
        and{' '}
        <a href="/privacy" className="text-blue-400 hover:text-blue-300">
          Privacy Policy
        </a>
        .
      </motion.p>
    </div>
  );
};

export default LoginForm;